<template>
  <div
    class="admin-component relative"
    :class="{
      'component-selected': isSelected,
      'component-hover': isHovered && !isSelected,
      'component-outline': showOutlines
    }"
    @click.stop="handleComponentClick"
    @mouseenter="handleComponentMouseEnter"
    @mouseleave="handleComponentMouseLeave"
    @keydown="handleKeyDown"
    tabindex="0"
    role="button"
    :aria-pressed="isSelected"
    :aria-label="`${getComponentName(component.componentType)} — ${isSelected ? t('admin.a11y.selectedPressEnterToEdit') : t('admin.a11y.pressEnterToSelect')}`"
  >
    <!-- Selection Overlay -->
    <div 
      v-if="isSelected" 
      class="selection-overlay absolute inset-0 pointer-events-none z-10"
    >
      <!-- Selection Border -->
      <div class="absolute inset-0 border-2 border-admin-primary bg-admin-primary/5"></div>
      
      <!-- Component Label -->
      <div class="absolute -top-8 left-0 bg-admin-primary text-white text-xs px-2 py-1 rounded-t flex items-center space-x-2">
        <i :class="getComponentIcon(component.componentType)" class="text-xs"></i>
        <span>{{ getComponentName(component.componentType) }}</span>
      </div>
      
      <!-- Action Toolbar -->
      <!-- pointer-events-auto: the overlay is pointer-events-none, which the toolbar
           would otherwise inherit (buttons + drag handle unreachable). -->
      <div class="absolute -top-8 right-0 bg-white border border-gray-200 rounded-t shadow-sm flex pointer-events-auto">
        <Button
          icon="pi pi-pencil"
          size="small"
          text
          class="h-8 w-8 hover:bg-gray-50"
          @click.stop="handleEditClick"
          :aria-label="t('admin.edit')"
          v-tooltip="'Edit'"
        />
        <Button
          icon="pi pi-copy"
          size="small"
          text
          class="h-8 w-8 hover:bg-gray-50"
          @click.stop="handleDuplicateClick"
          :aria-label="t('admin.duplicate')"
          :loading="duplicateComponentMutation.isPending.value"
          v-tooltip="'Duplicate'"
        />
        <Button
          icon="pi pi-arrows-v"
          size="small"
          text
          class="h-8 w-8 hover:bg-gray-50 cursor-move drag-handle"
          :aria-label="t('admin.dragToReorder')"
          v-tooltip="'Drag to reorder'"
        />
        <Button
          icon="pi pi-trash"
          size="small"
          text
          class="h-8 w-8 hover:bg-red-50 hover:text-red-600"
          @click.stop="confirmDelete"
          :aria-label="t('admin.delete')"
          v-tooltip="'Delete'"
        />
      </div>
    </div>
    
    <!-- Hover Overlay -->
    <div
      v-else-if="isHovered"
      class="hover-overlay absolute inset-0 pointer-events-none border border-admin-secondary/50 bg-admin-secondary/5"
    >
      <span class="absolute top-2 left-2 inline-flex items-center gap-1 rounded bg-admin-secondary px-2 py-1 text-xs font-medium text-white shadow">
        <i class="pi pi-pencil text-[10px]"></i>
        {{ t('admin.clickToEdit') }}
      </span>
    </div>
    
    <!-- Actual Component from Shared Library. pointer-events:none in the editor makes
         the whole component a single click target (selecting via the wrapper) and stops
         its internal CTA links/buttons from firing — which otherwise navigate the owner
         clean out of the editor. The wrapper's @click still selects because the event
         retargets to the wrapper.
         `inert` + aria-hidden additionally take the rendered preview OUT of the keyboard
         tab order and the accessibility tree (C1): pointer-events:none only blocks the
         MOUSE, so a keyboard/screen-reader user could still Tab into the live preview's
         non-functional CTA/Send buttons (a keyboard trap the blind panel hit). The
         wrapper itself stays focusable/selectable — only the inner preview is inert. -->
    <component
      :is="getSharedComponent(component.componentType)"
      v-bind="componentProps"
      :key="component.id"
      :style="{ pointerEvents: 'none' }"
      inert
      aria-hidden="true"
      @click.stop="handleSharedComponentClick"
    />
    
    <!-- Inline Edit Overlay -->
    <div 
      v-if="isInlineEditing"
      class="inline-edit-overlay absolute inset-0 z-20"
      @click.stop
    >
      <!-- This will be populated with contentEditable fields -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConfirm } from 'primevue/useconfirm'
import Button from 'primevue/button'
import { componentLogs } from '@/utils/logger'
import { useDeleteComponentMutation, useDuplicateComponentMutation } from '@/composables/usePages'
import { useUIStore } from '@/stores/ui'
import { getComponentDisplayName, getComponentIcon } from '@/utils/componentDisplayName'

// Import shared components and central registry from main package
import {
  componentMap,
  // Provide theme and site config via dependency injection
  THEME_CONFIG_KEY,
  SITE_CONFIG_KEY
} from '@aannemerplus/shared-components'

// Use UI store for state management

interface Props {
  component: {
    id: number
    componentType: string
    contentData: any
    position: number
    pageId?: number
  }
  isSelected?: boolean
  isHovered?: boolean
  showOutlines?: boolean
}

const props = defineProps<Props>()

// Only keep essential emissions for parent communication
const emit = defineEmits<{
  edit: [component: any]  // Still need to communicate edit mode to parent
}>()


const confirm = useConfirm()
const { t } = useI18n()
const isInlineEditing = ref(false)

// API mutations for direct operations
const deleteComponentMutation = useDeleteComponentMutation()
const duplicateComponentMutation = useDuplicateComponentMutation()

// UI store for state management
const uiStore = useUIStore()

// Computed properties for reactive selection state - use props or fallback to store
const isSelected = computed(() => props.isSelected ?? (uiStore.selectedComponent?.id === props.component.id))
const isHovered = computed(() => props.isHovered ?? (uiStore.hoveredComponent === props.component.id))

// Component mapping imported from central registry
// This ensures automatic synchronization with SectionBuilder.vue

// Computed properties
const getSharedComponent = computed(() => (type: string) => {
  return componentMap[type] || 'div'
})

const componentProps = computed(() => {
  const baseProps = { ...props.component.contentData }

  // Merge pending changes for real-time preview
  const pending = uiStore.pendingChanges.get(props.component.id)
  if (pending) {
    Object.assign(baseProps, pending)
  }

  // Add any admin-specific props here
  baseProps.__adminMode = true
  baseProps.__componentId = props.component.id

  return baseProps
})

// Helper functions — friendly names/icons come from the shared util (single source
// of truth; was duplicated + drifted, missing ~11 newer component types).
const getComponentName = getComponentDisplayName

const confirmDelete = () => {
  confirm.require({
    message: `Are you sure you want to delete this ${getComponentName(props.component.componentType)}?`,
    header: 'Delete Component',
    icon: 'pi pi-trash',
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Delete',
      severity: 'danger'
    },
    accept: () => {
      handleDeleteComponent()
    }
  })
}

const handleDeleteComponent = async () => {
  if (!props.component.pageId) {
    console.error('Cannot delete component: pageId is missing')
    return
  }

  try {
    await deleteComponentMutation.mutateAsync({
      componentId: props.component.id,
      pageId: props.component.pageId
    })

    // Clear selection if this component was selected
    if (uiStore.selectedComponent?.id === props.component.id) {
      uiStore.clearSelection()
    }

    console.log('Component deleted successfully:', props.component.id)
  } catch (error) {
    console.error('Failed to delete component:', error)
  }
}

// Direct event handlers - no more unnecessary emissions
const handleEditClick = (event?: Event) => {
  componentLogs.componentWrapper.editClicked(props.component)

  event?.stopPropagation()

  // Select the component first
  selectComponent(props.component)

  // Notify parent about edit mode for any special handling
  emit('edit', props.component)
}

const handleDuplicateClick = async (event?: Event) => {
  componentLogs.componentWrapper.duplicateClicked(props.component)

  event?.stopPropagation()

  // Inline (non-instance) grid items have synthetic string ids — nothing to copy server-side.
  if (typeof props.component.id !== 'number' || !props.component.pageId) {
    console.error('Cannot duplicate component: not a saved instance or pageId missing')
    return
  }
  if (duplicateComponentMutation.isPending.value) return

  try {
    const copy = await duplicateComponentMutation.mutateAsync({
      componentId: props.component.id,
      pageId: props.component.pageId
    })
    // Select the new copy so the owner lands on what they just created.
    selectComponent(copy)
  } catch (error) {
    console.error('Failed to duplicate component:', error)
  }
}

// Direct selection and hover management using store
const selectComponent = (component: any) => {
  console.log('Component selected:', component.id, component.componentType)
  uiStore.selectComponent({
    id: component.id,
    type: component.componentType,
    name: getComponentName(component.componentType),
    data: component.contentData
  })
}

const setHoveredComponent = (componentId: number | null) => {
  uiStore.hoverComponent(componentId)
}

const handleSharedComponentClick = (event: MouseEvent) => {
  componentLogs.componentWrapper.sharedComponentClicked(props.component)

  event.stopPropagation()
  selectComponent(props.component)
}

// Main component interaction handlers
const handleComponentClick = (event: MouseEvent) => {
  componentLogs.componentWrapper.clicked(props.component, event.target?.constructor.name || 'unknown', isSelected.value)

  event.stopPropagation()
  selectComponent(props.component)
}

const handleComponentMouseEnter = () => {
  componentLogs.componentWrapper.mouseEnter(props.component)
  setHoveredComponent(props.component.id)
}

const handleComponentMouseLeave = () => {
  componentLogs.componentWrapper.mouseLeave(props.component)
  setHoveredComponent(null)
}

// Handle keyboard shortcuts directly
const handleKeyDown = (event: KeyboardEvent) => {
  // Enter / Space: SELECT this component (or, if already selected, open its editor).
  // Without this a keyboard user could focus a component but never select it — the
  // whole edit flow was mouse-only (blind-panel finding).
  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault()
    if (uiStore.selectedComponent?.id !== props.component.id) {
      selectComponent(props.component)
    } else {
      handleEditClick()
    }
    return
  }

  // Remaining shortcuts only apply once this component is selected.
  if (uiStore.selectedComponent?.id !== props.component.id) return

  // Escape - clear selection
  if (event.key === 'Escape') {
    console.log('Escape pressed - clearing selection')
    uiStore.clearSelection()
    event.preventDefault()
  }

  // Delete - delete component
  if (event.key === 'Delete' || event.key === 'Backspace') {
    console.log('Delete key pressed - confirming deletion')
    confirmDelete()
    event.preventDefault()
  }

  // Ctrl/Cmd + D - duplicate component
  if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
    event.preventDefault()
    handleDuplicateClick(event)
  }
}
</script>

<style scoped>
.admin-component {
  position: relative;
}

.admin-component:hover .hover-overlay {
  opacity: 1;
}

.component-outline {
  outline: 1px dashed rgba(107, 114, 128, 0.3);
  outline-offset: 2px;
}

.component-selected {
  z-index: 10;
}

.component-hover {
  z-index: 5;
}

.selection-overlay {
  border-radius: inherit;
}

.hover-overlay {
  border-radius: inherit;
}

/* Drag handle cursor */
.drag-handle:hover {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

/* Prevent text selection during dragging */
.admin-component.dragging {
  user-select: none;
}

/* Smooth transitions */
.admin-component {
  transition: all 0.2s ease;
}

.selection-overlay,
.hover-overlay {
  transition: opacity 0.2s ease;
}
</style>